// App.tsx - نسخة معدلة مع إضافة خطوتي إنشاء وإغلاق الفاتورة (لتسجيل القيد المحاسبي)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from './api';
import Sidebar from './components/SidebarNew';
import UserInfo from './UserInfo';
import DrawerButton from './DrawerButton';
import SearchComponent from './SearchComponent';
import { API_URL, getAuthHeaders, isAuthenticated } from './config';
import { safeArray, safeFilter, safeFind } from './utils/safeArray';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { formatEgyptDate } from './utils/dateUtils';
import { getTaxSettings, TaxSettings } from './taxUtils';
import './App.css';

// مفاتيح التخزين المؤقت للسلة
const CART_STORAGE_KEY = 'pos_main_cart';
const SECONDARY_CART_STORAGE_KEY = 'pos_secondary_cart';
const CART_TIMESTAMP_KEY = 'pos_cart_timestamp';

interface Product {
  id: number;
  name_ar: string;
  name_en: string;
  price: number;
  cost: number;
  category_id: number;
  stock_quantity: number;
  barcode?: string | null;
  taxable: boolean;
  product_type?: 'normal' | 'recipe';
  is_active?: boolean;
  min_stock_level?: number;
  max_stock_level?: number;
  reorder_point?: number;
}

interface Category {
  id: number;
  name_ar: string;
  name_en: string;
}

interface Customer {
  id: number;
  name: string;
  phone: string;
  current_balance?: number;
  credit_limit?: number;
}

interface CartItem {
  product_id: number;
  name_ar: string;
  price: number;
  cost: number;
  quantity: number;
  stock_quantity: number;
  is_cafe: boolean;
  apply_tax: boolean;
  apply_service: boolean;
  notes?: string;
  product_type?: 'normal' | 'recipe';
  max_available?: number;
}

interface SaleData {
  sale_number?: string;
  customer_id?: number;
  customer_name?: string;
  customer_phone?: string;
  discount_amount: number;
  payment_method: string;
  payment_status: string;
  paid_amount: number;
  items: any[];
  tax_rate: number;
  service_rate: number;
  total_after_tax?: number;
  tax_amount?: number;
  service_amount?: number;
  notes?: string;
  apply_customer_credit?: boolean;
  credit_to_apply?: number;
  customer_credit_used?: number;
}

interface Setting {
  key: string;
  value: string;
}

interface PrinterSettings {
  invoice_printer_type: 'windows' | 'usb' | 'ip';
  invoice_printer_name: string;
  invoice_printer_usb_vid: string;
  invoice_printer_usb_pid: string;
  invoice_printer_ip: string;
  invoice_printer_port: number;
  barcode_printer_type: 'windows' | 'usb' | 'ip';
  barcode_printer_name: string;
  barcode_printer_usb_vid: string;
  barcode_printer_usb_pid: string;
  barcode_printer_ip: string;
  barcode_printer_port: number;
  kitchen_printer_type: 'windows' | 'usb' | 'ip';
  kitchen_printer_name: string;
  kitchen_printer_usb_vid: string;
  kitchen_printer_usb_pid: string;
  kitchen_printer_ip: string;
  kitchen_printer_port: number;
  barcode_width: number;
  barcode_height: number;
  barcode_font_size: number;
  barcode_text_align: 'left' | 'center' | 'right';
  barcode_align: 'left' | 'center' | 'right';
  margin_top: number;
  margin_bottom: number;
  margin_right: number;
  margin_left: number;
  paper_type: 'single' | 'precut';
  label_width: number;
  label_height: number;
  columns_count: number;
  rows_count: number;
  gap_horizontal: number;
  gap_vertical: number;
  start_column: number;
  start_row: number;
  invoice_width: number;
  invoice_font_size: number;
  invoice_align: 'left' | 'center' | 'right';
  company_name: string;
  company_phone: string;
  company_address: string;
  show_logo: boolean;
  show_phone: boolean;
  show_address: boolean;
  show_footer: boolean;
  barcode_type: string;
  kitchen_paper_width: number;
  kitchen_auto_print: boolean;
  kitchen_enabled: boolean;
  use_direct_print: boolean;
}

interface KitchenSettings {
  enabled: boolean;
  printer_name: string;
  auto_print: boolean;
}

function App() {
  useTokenRefresh(60000);

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [settings, setSettings] = useState<{ [key: string]: string }>({});
  const [printerSettings, setPrinterSettings] = useState<PrinterSettings | null>(null);
  const [taxSettings, setTaxSettings] = useState<TaxSettings>({
    tax_rate: 14,
    service_rate: 12,
    apply_tax_to_pos: true,
    apply_service_to_pos: true,
    apply_tax_to_tables: true,
    apply_service_to_tables: true
  });
  const [kitchenSettings, setKitchenSettings] = useState<KitchenSettings>({
    enabled: false,
    printer_name: '',
    auto_print: true
  });
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const [cart, setCart] = useState<CartItem[]>([]);
  const [secondaryCart, setSecondaryCart] = useState<CartItem[]>([]);
  const [activeCart, setActiveCart] = useState<'main' | 'secondary'>('main');
  const [hasMultipleInvoices, setHasMultipleInvoices] = useState(false);

  const [barcodeInput, setBarcodeInput] = useState('');
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [paidAmount, setPaidAmount] = useState<number>(0);

  const [secondaryDiscountAmount, setSecondaryDiscountAmount] = useState<number>(0);
  const [secondaryPaymentMethod, setSecondaryPaymentMethod] = useState<string>('cash');
  const [secondaryPaidAmount, setSecondaryPaidAmount] = useState<number>(0);

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showSearch, setShowSearch] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [secondaryCustomer, setSecondaryCustomer] = useState<Customer | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '' });
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // حالات رصيد العميل
  const [customerCreditBalance, setCustomerCreditBalance] = useState(0);
  const [showCreditAlert, setShowCreditAlert] = useState(false);
  const [applyCustomerCredit, setApplyCustomerCredit] = useState(false);
  const [creditToApply, setCreditToApply] = useState(0);

  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productSearchRef = useRef<HTMLDivElement>(null);

  const isInitialMount = useRef(true);
  const dataLoadedRef = useRef(false);
  const [productList, setProductList] = useState<Product[]>([]);
  const maxQuantityCache = useRef<Map<number, number>>(new Map());

  // ========== حفظ واستعادة السلة من localStorage ==========
  const loadSavedCart = useCallback(() => {
    try {
      const timestamp = localStorage.getItem(CART_TIMESTAMP_KEY);
      if (timestamp) {
        const savedTime = parseInt(timestamp);
        const now = Date.now();
        const hourInMs = 60 * 60 * 1000;
        if (now - savedTime > hourInMs) {
          localStorage.removeItem(CART_STORAGE_KEY);
          localStorage.removeItem(SECONDARY_CART_STORAGE_KEY);
          localStorage.removeItem(CART_TIMESTAMP_KEY);
          return;
        }
      }
      const savedMainCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedMainCart) {
        const parsed = JSON.parse(savedMainCart);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCart(parsed);
        }
      }
      const savedSecondaryCart = localStorage.getItem(SECONDARY_CART_STORAGE_KEY);
      if (savedSecondaryCart) {
        const parsed = JSON.parse(savedSecondaryCart);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSecondaryCart(parsed);
          setHasMultipleInvoices(true);
        }
      }
    } catch (error) {
      console.error('خطأ في تحميل السلة المحفوظة:', error);
    }
  }, []);

  useEffect(() => {
    loadSavedCart();
    isInitialMount.current = false;
  }, []);

  useEffect(() => {
    if (!isInitialMount.current) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
      localStorage.setItem(CART_TIMESTAMP_KEY, Date.now().toString());
    }
  }, [cart]);

  useEffect(() => {
    if (!isInitialMount.current) {
      localStorage.setItem(SECONDARY_CART_STORAGE_KEY, JSON.stringify(secondaryCart));
      localStorage.setItem(CART_TIMESTAMP_KEY, Date.now().toString());
    }
  }, [secondaryCart]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productSearchRef.current && !productSearchRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDateTime = (date: Date) => {
    return formatEgyptDate(date.toISOString());
  };

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get(`/api/settings`);
      const settingsMap: { [key: string]: string } = {};
      if (Array.isArray(response.data)) {
        response.data.forEach((setting: Setting) => {
          if (setting && setting.key) {
            settingsMap[setting.key] = setting.value || '';
          }
        });
      } else if (response.data && typeof response.data === 'object') {
        Object.entries(response.data).forEach(([key, value]) => {
          settingsMap[key] = String(value || '');
        });
      }
      setSettings(settingsMap);
    } catch (error) {
      console.error('خطأ في جلب الإعدادات:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get(`/api/products?include_inactive=true`);
      const productsData = Array.isArray(response.data) ? response.data : [];
      setProducts(productsData);
      setProductList(productsData);
    } catch (error) {
      console.error('خطأ في جلب المنتجات:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get(`/api/categories`);
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('خطأ في جلب التصنيفات:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get(`/api/customers`);
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('خطأ في جلب العملاء:', error);
    }
  }, []);

  const loadTaxSettings = useCallback(async () => {
    try {
      const settings = await getTaxSettings();
      setTaxSettings(settings);
      console.log('✅ تم تحميل إعدادات الضريبة المركزية:', settings);
    } catch (error) {
      console.error('خطأ في تحميل إعدادات الضريبة:', error);
    }
  }, []);

  const loadPrinterSettings = useCallback(() => {
    try {
      const saved = localStorage.getItem('printer_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        setPrinterSettings(parsed);
      }
    } catch (error) {
      console.error('خطأ في تحميل إعدادات الطابعة:', error);
    }
  }, []);

  const loadKitchenSettings = useCallback(() => {
    try {
      const saved = localStorage.getItem('kitchen_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        setKitchenSettings(parsed);
      }
    } catch (error) {
      console.error('خطأ في تحميل إعدادات المطبخ:', error);
    }
  }, []);

  // ========== دوال الحصول على السلة والإعدادات الحالية ==========
  const getCurrentCart = useCallback(() => {
    return activeCart === 'main' ? cart : secondaryCart;
  }, [activeCart, cart, secondaryCart]);

  const getCurrentDiscount = useCallback(() => {
    return activeCart === 'main' ? discountAmount : secondaryDiscountAmount;
  }, [activeCart, discountAmount, secondaryDiscountAmount]);

  const getCurrentPaymentMethod = useCallback(() => {
    return activeCart === 'main' ? paymentMethod : secondaryPaymentMethod;
  }, [activeCart, paymentMethod, secondaryPaymentMethod]);

  const getCurrentPaidAmount = useCallback(() => {
    return activeCart === 'main' ? paidAmount : secondaryPaidAmount;
  }, [activeCart, paidAmount, secondaryPaidAmount]);

  const setCurrentPaidAmount = useCallback((amount: number) => {
    if (activeCart === 'main') {
      setPaidAmount(amount);
    } else {
      setSecondaryPaidAmount(amount);
    }
  }, [activeCart]);

  const getCurrentCustomer = useCallback(() => {
    return activeCart === 'main' ? selectedCustomer : secondaryCustomer;
  }, [activeCart, selectedCustomer, secondaryCustomer]);

  const setCurrentDiscount = useCallback((amount: number) => {
    if (activeCart === 'main') {
      setDiscountAmount(amount);
    } else {
      setSecondaryDiscountAmount(amount);
    }
  }, [activeCart]);

  const setCurrentPaymentMethod = useCallback((method: string) => {
    if (activeCart === 'main') {
      setPaymentMethod(method);
    } else {
      setSecondaryPaymentMethod(method);
    }
  }, [activeCart]);

  // ========== دوال الحساب ==========
  const calculateSubtotal = useCallback((items: CartItem[]) => {
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, []);

  const calculateTaxableTotal = useCallback((items: CartItem[]) => {
    return items.reduce(
      (sum, item) => sum + (item.apply_tax ? item.price * item.quantity : 0),
      0
    );
  }, []);

  const calculateServiceTotal = useCallback((items: CartItem[]) => {
    return items.reduce(
      (sum, item) => sum + (item.apply_service ? item.price * item.quantity : 0),
      0
    );
  }, []);

  const calculateTax = useCallback((items: CartItem[]) => {
    return taxSettings.apply_tax_to_pos ? calculateTaxableTotal(items) * (taxSettings.tax_rate / 100) : 0;
  }, [taxSettings, calculateTaxableTotal]);

  const calculateService = useCallback((items: CartItem[]) => {
    return taxSettings.apply_service_to_pos ? calculateServiceTotal(items) * (taxSettings.service_rate / 100) : 0;
  }, [taxSettings, calculateServiceTotal]);

  const calculateTotal = useCallback((items: CartItem[], discount: number) => {
    const subtotal = calculateSubtotal(items);
    const afterDiscount = subtotal - discount;
    return afterDiscount + calculateTax(items) + calculateService(items);
  }, [calculateSubtotal, calculateTax, calculateService]);

  // ========== دوال رصيد العميل ==========
  const fetchCustomerCreditBalance = useCallback(async (customerId: number) => {
    try {
      const response = await api.get(`${API_URL}/api/customers/${customerId}/full-balance`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        const creditBalance = response.data.credit_balance || 0;
        setCustomerCreditBalance(creditBalance);
        return creditBalance;
      }
      return 0;
    } catch (error) {
      console.error('خطأ في جلب رصيد العميل:', error);
      return 0;
    }
  }, []);

  const applyCreditToCurrentSale = useCallback(() => {
    const currentCart = getCurrentCart();
    const total = calculateTotal(currentCart, getCurrentDiscount());
    if (customerCreditBalance > 0) {
      const creditToUse = Math.min(customerCreditBalance, total);
      setApplyCustomerCredit(true);
      setCreditToApply(creditToUse);
      setShowCreditAlert(false);
      if (getCurrentPaymentMethod() === 'credit') {
        setCurrentPaidAmount(getCurrentPaidAmount() + creditToUse);
      }
      setMessage(`✅ تم تطبيق رصيد بقيمة ${creditToUse.toFixed(2)} ج.م من مرتجعات سابقة`);
      setTimeout(() => setMessage(''), 3000);
    }
  }, [customerCreditBalance, getCurrentCart, calculateTotal, getCurrentDiscount, getCurrentPaymentMethod, getCurrentPaidAmount, setCurrentPaidAmount]);

  // ========== دوال العملاء ==========
  const selectCustomer = useCallback(async (customer: Customer) => {
    if (activeCart === 'main') {
      setSelectedCustomer(customer);
    } else {
      setSecondaryCustomer(customer);
    }
    setCustomerSearch('');
    setCustomerSearchResults([]);
    setShowCustomerResults(false);
    setApplyCustomerCredit(false);
    setCreditToApply(0);
    setShowCreditAlert(false);
    
    const balance = await fetchCustomerCreditBalance(customer.id);
    if (balance > 0) {
      setShowCreditAlert(true);
    }
  }, [activeCart, fetchCustomerCreditBalance]);

  const clearCustomer = useCallback(() => {
    if (activeCart === 'main') {
      setSelectedCustomer(null);
    } else {
      setSecondaryCustomer(null);
    }
    setApplyCustomerCredit(false);
    setCreditToApply(0);
    setShowCreditAlert(false);
    setCustomerCreditBalance(0);
  }, [activeCart]);

  const createCustomer = useCallback(async () => {
    if (!newCustomer.name.trim() || !newCustomer.phone.trim()) {
      alert('⚠️ الرجاء إدخال الاسم ورقم الهاتف');
      return;
    }
    try {
      const response = await api.post(`/api/customers`, {
        name: newCustomer.name.trim(),
        phone: newCustomer.phone.trim(),
        customer_type: 'cash'
      });
      if (response.data) {
        const newCustomerObj = response.data;
        if (activeCart === 'main') {
          setSelectedCustomer(newCustomerObj);
        } else {
          setSecondaryCustomer(newCustomerObj);
        }
        setShowCustomerModal(false);
        setNewCustomer({ name: '', phone: '' });
        const updatedCustomers = await api.get(`/api/customers`);
        setCustomers(Array.isArray(updatedCustomers.data) ? updatedCustomers.data : []);
        setMessage('✅ تم إضافة العميل بنجاح');
        setTimeout(() => setMessage(''), 3000);
        
        const balance = await fetchCustomerCreditBalance(newCustomerObj.id);
        if (balance > 0) {
          setShowCreditAlert(true);
        } else {
          setShowCreditAlert(false);
        }
      }
    } catch (error: any) {
      setMessage('❌ فشل في إضافة العميل: ' + (error.response?.data?.detail || error.message));
      setTimeout(() => setMessage(''), 3000);
    }
  }, [newCustomer, activeCart, fetchCustomerCreditBalance]);

  // ========== دوال الفواتير المتعددة ==========
  const openNewInvoice = useCallback(() => {
    if (!hasMultipleInvoices) {
      setHasMultipleInvoices(true);
      setActiveCart('secondary');
      setMessage('✅ تم فتح فاتورة جانبية جديدة');
      setTimeout(() => setMessage(''), 2000);
    } else {
      alert('⚠️ يوجد بالفعل فاتورة جانبية مفتوحة');
    }
  }, [hasMultipleInvoices]);

  const switchToMain = useCallback(() => {
    setActiveCart('main');
  }, []);

  const switchToSecondary = useCallback(() => {
    setActiveCart('secondary');
  }, []);

  const closeSecondaryInvoice = useCallback(() => {
    if (secondaryCart.length > 0) {
      if (window.confirm('⚠️ هل أنت متأكد من إغلاق الفاتورة الجانبية؟ سيتم فقدان المنتجات المضافة')) {
        setSecondaryCart([]);
        setSecondaryDiscountAmount(0);
        setSecondaryPaidAmount(0);
        setSecondaryCustomer(null);
        setHasMultipleInvoices(false);
        setActiveCart('main');
        localStorage.removeItem(SECONDARY_CART_STORAGE_KEY);
      }
    } else {
      setHasMultipleInvoices(false);
      setActiveCart('main');
      localStorage.removeItem(SECONDARY_CART_STORAGE_KEY);
    }
  }, [secondaryCart]);

  // ========== دوال السلة والمنتجات ==========
  const searchCustomers = useCallback(async () => {
    if (customerSearch.length < 2) return;
    try {
      const response = await api.get(`/api/customers?search=${customerSearch}`);
      setCustomerSearchResults(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('خطأ في البحث عن العملاء:', error);
    }
  }, [customerSearch]);

  const handleCustomerSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerSearch(e.target.value);
    if (e.target.value.length >= 2) {
      setShowCustomerResults(true);
    } else {
      setShowCustomerResults(false);
    }
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    if (activeCart === 'main') {
      setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
    } else {
      setSecondaryCart(prevCart => prevCart.filter(item => item.product_id !== productId));
    }
  }, [activeCart]);

  const updateItemQuantity = useCallback((productId: number, newQuantity: number) => {
    if (activeCart === 'main') {
      setCart(prevCart =>
        prevCart.map(item =>
          item.product_id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    } else {
      setSecondaryCart(prevCart =>
        prevCart.map(item =>
          item.product_id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  }, [activeCart]);

  const checkRecipeAvailability = useCallback(async (productId: number, quantity: number): Promise<{ available: boolean; maxQuantity?: number; message?: string }> => {
    try {
      const response = await api.get(`/api/recipes/product/${productId}/check?quantity=${quantity}`);
      if (response.data.is_available) {
        return { available: true };
      }
      if (response.data.missing_materials && response.data.missing_materials.length > 0) {
        const missingMaterials = response.data.missing_materials;
        let maxQty = 0;
        for (const material of missingMaterials) {
          const shortage = material.shortage || 0;
          const required = material.required || quantity;
          if (shortage > 0 && required > 0) {
            const possible = Math.floor(quantity - (shortage / (required / quantity)));
            if (maxQty === 0 || possible < maxQty) {
              maxQty = possible;
            }
          }
        }
        if (maxQuantityCache.current.has(productId) && maxQuantityCache.current.get(productId)! < maxQty) {
          maxQty = maxQuantityCache.current.get(productId)!;
        }
        if (maxQty > 0) {
          maxQuantityCache.current.set(productId, maxQty);
          return { available: false, maxQuantity: maxQty, message: response.data.message };
        }
      }
      return { available: false, message: response.data.message || 'المنتج غير متوفر حالياً' };
    } catch (error) {
      console.error('خطأ في التحقق من توفر المنتج:', error);
      return { available: false, message: 'حدث خطأ في التحقق من توفر المنتج' };
    }
  }, []);

  const updateQuantity = useCallback(async (productId: number, newQuantity: number) => {
    const product = productList.find(p => p.id === productId);
    if (!product) return;

    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    if (product.product_type === 'normal') {
      if (newQuantity > product.stock_quantity) {
        alert(`⚠️ الكمية المتاحة فقط ${product.stock_quantity}`);
        return;
      }
      updateItemQuantity(productId, newQuantity);
      return;
    }

    if (product.product_type === 'recipe') {
      if (product.is_active === false) {
        alert('⚠️ هذا المنتج غير متوفر حالياً (نقص في المواد الخام)');
        return;
      }
      const result = await checkRecipeAvailability(productId, newQuantity);
      if (!result.available) {
        if (result.maxQuantity && result.maxQuantity > 0) {
          alert(`⚠️ لا يمكن إضافة هذه الكمية. الكمية القصوى المتاحة: ${result.maxQuantity}`);
          updateItemQuantity(productId, result.maxQuantity);
        } else {
          alert('⚠️ هذا المنتج غير متوفر حالياً');
        }
        return;
      }
      updateItemQuantity(productId, newQuantity);
    }
  }, [productList, updateItemQuantity, checkRecipeAvailability, removeFromCart]);

  const addToCart = useCallback(async (product: Product) => {
    if (product.product_type === 'recipe') {
      if (product.is_active === false) {
        alert('⚠️ هذا المنتج غير متوفر حالياً (نقص في المواد الخام)');
        return;
      }
      const currentQuantity = activeCart === 'main'
        ? cart.find(item => item.product_id === product.id)?.quantity || 0
        : secondaryCart.find(item => item.product_id === product.id)?.quantity || 0;
      const newQuantity = currentQuantity + 1;
      const result = await checkRecipeAvailability(product.id, newQuantity);
      if (!result.available) {
        if (result.maxQuantity && result.maxQuantity > 0) {
          if (result.maxQuantity > currentQuantity) {
            alert(`⚠️ يمكن إضافة ${result.maxQuantity - currentQuantity} فقط من هذا المنتج`);
            updateItemQuantity(product.id, result.maxQuantity);
          } else {
            alert(`⚠️ لا يمكن إضافة المزيد من هذا المنتج. الكمية القصوى المتاحة: ${result.maxQuantity}`);
          }
        } else {
          alert('⚠️ هذا المنتج غير متوفر حالياً');
        }
        return;
      }
    } else {
      if (product.stock_quantity === 0) {
        alert('⚠️ هذا المنتج غير متوفر حالياً');
        return;
      }
    }

    if (activeCart === 'main') {
      setCart(prevCart => {
        const currentInCart = prevCart.find(item => item.product_id === product.id)?.quantity || 0;
        if (product.product_type === 'normal' && currentInCart >= product.stock_quantity) {
          alert(`⚠️ الكمية المتاحة فقط ${product.stock_quantity}`);
          return prevCart;
        }
        const existingItemIndex = prevCart.findIndex(item => item.product_id === product.id);
        if (existingItemIndex >= 0) {
          const newCart = [...prevCart];
          newCart[existingItemIndex] = { ...newCart[existingItemIndex], quantity: newCart[existingItemIndex].quantity + 1 };
          return newCart;
        }
        return [...prevCart, {
          product_id: product.id,
          name_ar: product.name_ar,
          price: product.price,
          cost: product.cost,
          quantity: 1,
          stock_quantity: product.stock_quantity,
          is_cafe: product.taxable,
          apply_tax: product.taxable && taxSettings.apply_tax_to_pos,
          apply_service: taxSettings.apply_service_to_pos,
          notes: '',
          product_type: product.product_type,
          max_available: product.product_type === 'recipe' ? undefined : product.stock_quantity
        }];
      });
    } else {
      setSecondaryCart(prevCart => {
        const currentInCart = prevCart.find(item => item.product_id === product.id)?.quantity || 0;
        if (product.product_type === 'normal' && currentInCart >= product.stock_quantity) {
          alert(`⚠️ الكمية المتاحة فقط ${product.stock_quantity}`);
          return prevCart;
        }
        const existingItemIndex = prevCart.findIndex(item => item.product_id === product.id);
        if (existingItemIndex >= 0) {
          const newCart = [...prevCart];
          newCart[existingItemIndex] = { ...newCart[existingItemIndex], quantity: newCart[existingItemIndex].quantity + 1 };
          return newCart;
        }
        return [...prevCart, {
          product_id: product.id,
          name_ar: product.name_ar,
          price: product.price,
          cost: product.cost,
          quantity: 1,
          stock_quantity: product.stock_quantity,
          is_cafe: product.taxable,
          apply_tax: product.taxable && taxSettings.apply_tax_to_pos,
          apply_service: taxSettings.apply_service_to_pos,
          notes: '',
          product_type: product.product_type,
          max_available: product.product_type === 'recipe' ? undefined : product.stock_quantity
        }];
      });
    }
  }, [activeCart, cart, secondaryCart, taxSettings, checkRecipeAvailability, updateItemQuantity]);

  const toggleItemTaxable = useCallback((productId: number) => {
    if (activeCart === 'main') {
      setCart(prevCart =>
        prevCart.map(item =>
          item.product_id === productId ? { ...item, apply_tax: !item.apply_tax } : item
        )
      );
    } else {
      setSecondaryCart(prevCart =>
        prevCart.map(item =>
          item.product_id === productId ? { ...item, apply_tax: !item.apply_tax } : item
        )
      );
    }
  }, [activeCart]);

  const toggleItemService = useCallback((productId: number) => {
    if (activeCart === 'main') {
      setCart(prevCart =>
        prevCart.map(item =>
          item.product_id === productId ? { ...item, apply_service: !item.apply_service } : item
        )
      );
    } else {
      setSecondaryCart(prevCart =>
        prevCart.map(item =>
          item.product_id === productId ? { ...item, apply_service: !item.apply_service } : item
        )
      );
    }
  }, [activeCart]);

  const handleBarcodeSearch = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      e.preventDefault();
      const product = productList.find(p => p.barcode === barcodeInput.trim());
      if (product) {
        addToCart(product);
        setBarcodeInput('');
      } else {
        alert('⚠️ المنتج غير موجود');
      }
    }
  }, [barcodeInput, productList, addToCart]);

  const filteredProductsForSearch = useMemo(() => {
    if (!productSearchTerm.trim()) return [];
    const term = productSearchTerm.trim().toLowerCase();
    return products.filter(p => 
      p.name_ar.toLowerCase().includes(term) ||
      (p.barcode && p.barcode.toLowerCase().includes(term))
    ).slice(0, 10);
  }, [products, productSearchTerm]);

  const printKitchen = useCallback((items: CartItem[], tableNumber?: string, orderType: string = 'POS') => {
    const kitchenItems = items.filter(item => item.is_cafe === true);
    if (kitchenItems.length === 0) {
      console.log('🍳 لا توجد أصناف كافيه للطباعة');
      return;
    }
    console.log('🍳 جاري طباعة المطبخ...', kitchenItems);
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('⚠️ الرجاء السماح بالنوافذ المنبثقة للطباعة');
      return;
    }
    const now = new Date();
    const dateStr = now.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    const hasNotes = kitchenItems.some(item => item.notes && item.notes.trim() !== '');
    const notesHtml = hasNotes ? `
      <div style="
        margin-top: 10px; 
        padding: 8px; 
        background-color: #ffffff; 
        border: 1px solid #000000;
        border-radius: 0; 
        font-size: 12px;
        border-right: 3px solid #000000;
        color: #000000;
      ">
        📝 ملاحظات: ${kitchenItems.filter(i => i.notes && i.notes.trim() !== '').map(i => i.notes).join('، ')}
      </div>
    ` : '';
    printWindow.document.write(`
      <html dir="rtl">
        <head>
          <title>🍳 أمر مطبخ</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            body { font-family: 'Arial', sans-serif; padding: 10px; margin: 0; width: 80mm; margin: 0 auto; background: white; color: #000000; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000000; padding-bottom: 10px; }
            .header h1 { color: #000000; margin: 5px 0; font-size: 20px; }
            .header h2 { color: #000000; margin: 5px 0; font-size: 16px; }
            .header p { margin: 5px 0; font-size: 12px; color: #000000; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 14px; }
            th { background: #ffffff; color: #000000; padding: 8px; text-align: center; border-bottom: 2px solid #000000; }
            td { padding: 8px; border-bottom: 1px solid #000000; text-align: center; }
            .footer { text-align: center; margin-top: 20px; color: #000000; font-size: 10px; border-top: 1px solid #000000; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🍳 أمر مطبخ</h1>
            <h2>${orderType === 'table' ? `ترابيزة ${tableNumber}` : 'طلبية كاشير'}</h2>
            <p>${dateStr}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>الصنف</th>
                <th>الكمية</th>
              </tr>
            </thead>
            <tbody>
              ${kitchenItems.map(item => `
                <tr>
                  <td>${item.name_ar}${item.notes ? ` (${item.notes})` : ''}</td>
                  <td>${item.quantity}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          ${notesHtml}
          
          <div class="footer">
            <p>طابعة المطبخ: ${kitchenSettings.printer_name || 'غير محددة'}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, [kitchenSettings]);

  const printInvoice = useCallback((saleData: SaleData, items: CartItem[], subtotal: number, tax: number, service: number, total: number) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('⚠️ الرجاء السماح بالنوافذ المنبثقة للطباعة');
      return;
    }

    const now = new Date();
    const dateStr = formatEgyptDate(now.toISOString());

    const paymentMethodMap: { [key: string]: string } = {
      'cash': 'كاش',
      'visa': 'فيزا',
      'wallet': 'محفظة',
      'credit': 'أجل'
    };
    const paymentMethodText = paymentMethodMap[saleData.payment_method] || saleData.payment_method;
    const afterDiscount = subtotal - saleData.discount_amount;

    const customerInfo = saleData.customer_name 
      ? `<div><strong>العميل:</strong> ${saleData.customer_name}</div>
         ${saleData.customer_phone ? `<div><strong>الهاتف:</strong> ${saleData.customer_phone}</div>` : ''}`
      : '<div><strong>العميل:</strong> نقدي</div>';

    const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const sellerName = currentUser.username || 'admin';

    let itemsHtml = items.map(item => `
      <tr>
        <td style="padding: 5px 3px; border-bottom: 1px solid #000000;">${item.name_ar}${item.notes ? `<div style="font-size: 10px; color: #000000;">📝 ${item.notes}</div>` : ''}</td>
        <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #000000;">${item.quantity}</td>
        <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #000000;">${item.price.toFixed(2)}</td>
        <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #000000; font-weight: bold;">${(item.price * item.quantity).toFixed(2)}</td>
      </tr>
    `).join('');

    let totalsHtml = `
      <div style="display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px dashed #000000;">
        <span>الإجمالي قبل الخصم:</span><span>${subtotal.toFixed(2)} ج.م</span>
      </div>
    `;
    if (saleData.discount_amount > 0) {
      totalsHtml += `
        <div style="display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px dashed #000000;">
          <span>الخصم:</span><span>- ${saleData.discount_amount.toFixed(2)} ج.م</span>
        </div>
      `;
    }
    // ✅ إضافة سطر الرصيد المستخدم من مرتجعات العميل
    if (saleData.customer_credit_used && saleData.customer_credit_used > 0) {
      totalsHtml += `
        <div style="display: flex; justifyContent: space-between; padding: 3px 0; border-bottom: 1px dashed #000000;">
          <span>💳 رصيد مستخدم من مرتجعات سابقة:</span><span>- ${saleData.customer_credit_used.toFixed(2)} ج.م</span>
        </div>
      `;
    }
    if (tax > 0) {
      totalsHtml += `
        <div style="display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px dashed #000000;">
          <span>ضريبة (${saleData.tax_rate}%):</span><span>+ ${tax.toFixed(2)} ج.م</span>
        </div>
      `;
    }
    if (service > 0) {
      totalsHtml += `
        <div style="display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px dashed #000000;">
          <span>خدمة (${saleData.service_rate}%):</span><span>+ ${service.toFixed(2)} ج.م</span>
        </div>
      `;
    }

    const logoHtml = settings.company_logo && settings.company_logo.startsWith('http')
      ? `<img src="${settings.company_logo}" style="max-width: 120px; max-height: 60px; margin-bottom: 5px;" onerror="this.style.display='none'">`
      : '';

    printWindow.document.write(`
      <html dir="rtl">
        <head>
          <title>فاتورة بيع - ${saleData.sale_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Arial', sans-serif; 
              background: white; 
              display: flex;
              justify-content: center;
              padding: 0;
              margin: 0;
              color: #000000;
            }
            .invoice-container { 
              max-width: 80mm; 
              width: 100%;
              background: white;
              padding: 5px 3px;
              border: none;
            }
            .header { 
              text-align: center; 
              margin-bottom: 8px; 
              border-bottom: 2px solid #000000; 
              padding-bottom: 5px; 
            }
            .header h1 { 
              color: #000000; 
              margin: 2px 0; 
              font-size: 16px; 
              font-weight: bold;
            }
            .header h2 { 
              color: #000000; 
              margin: 2px 0; 
              font-size: 14px; 
              font-weight: bold;
            }
            .header p { 
              margin: 2px 0; 
              font-size: 11px; 
              color: #000000; 
            }
            .logo { text-align: center; margin-bottom: 5px; }
            .customer-info, .payment-method, .seller-info { 
              border: 1px solid #000000;
              padding: 5px; 
              border-radius: 0; 
              margin: 5px 0; 
              font-size: 11px; 
              color: #000000;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 8px 0; 
              font-size: 11px; 
              color: #000000;
            }
            th { 
              background: #ffffff; 
              color: #000000; 
              padding: 5px; 
              font-size: 11px; 
              font-weight: bold;
              border-bottom: 2px solid #000000;
            }
            td { 
              padding: 5px 3px; 
              border-bottom: 1px solid #000000; 
              color: #000000;
            }
            .total-section { 
              margin-top: 5px; 
              border-top: 2px solid #000000; 
              padding-top: 5px; 
            }
            .total-row { 
              display: flex; 
              justify-content: space-between; 
              padding: 3px 0; 
              font-size: 11px; 
              color: #000000;
              border-bottom: 1px dashed #000000;
            }
            .grand-total { 
              font-size: 14px; 
              font-weight: bold; 
              color: #000000; 
              border-top: 2px solid #000000; 
              padding-top: 5px; 
              margin-top: 3px; 
            }
            .footer { 
              text-align: center; 
              margin-top: 8px; 
              color: #000000; 
              font-size: 10px; 
              border-top: 1px solid #000000; 
              padding-top: 5px; 
            }
            @media print { 
              body { padding: 0; } 
              .invoice-container { border: none; } 
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="logo">${logoHtml}</div>
            <div class="header">
              <h1>${settings.app_name || 'متجر'}</h1>
              <h2>فاتورة بيع رقم: ${saleData.sale_number}</h2>
              <p>التاريخ: ${dateStr}</p>
            </div>
            
            <div class="customer-info">
              ${customerInfo}
            </div>
            
            <div class="seller-info">
              <strong>البائع:</strong> ${sellerName}
            </div>
            
            <div class="payment-method">
              <strong>طريقة الدفع:</strong> ${paymentMethodText}
              ${saleData.payment_status === 'partial' ? `<br><strong>المدفوع:</strong> ${saleData.paid_amount} ج.م` : ''}
              ${saleData.payment_status === 'partial' ? `<br><strong>المتبقي:</strong> ${(total - (saleData.paid_amount || 0)).toFixed(2)} ج.م` : ''}
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>الصنف</th>
                  <th>كمية</th>
                  <th>سعر</th>
                  <th>إجمالي</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
            </table>
            
            <div class="total-section">
              ${totalsHtml}
              <div class="total-row grand-total"><span>الإجمالي النهائي:</span><span>${total.toFixed(2)} ج.م</span></div>
            </div>
            
            <div class="footer">
              <p>${settings.invoice_footer || 'شكراً لتعاملكم معنا'}</p>
              ${settings.company_address ? `<p>${settings.company_address}</p>` : ''}
              ${settings.company_phone ? `<p>هاتف: ${settings.company_phone}</p>` : ''}
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, [settings]);

  const resetCurrentCart = useCallback(() => {
    if (activeCart === 'main') {
      setCart([]);
      setDiscountAmount(0);
      setPaidAmount(0);
      setSelectedCustomer(null);
      setApplyCustomerCredit(false);
      setCreditToApply(0);
      setShowCreditAlert(false);
      setCustomerCreditBalance(0);
    } else {
      setSecondaryCart([]);
      setSecondaryDiscountAmount(0);
      setSecondaryPaidAmount(0);
      setSecondaryCustomer(null);
    }
  }, [activeCart]);

  // ========== دوال إتمام البيع (المعدلة لاستخدام خطوتي إنشاء وإغلاق الفاتورة) ==========
  const completeSale = useCallback(async () => {
  const currentCart = getCurrentCart();
  if (currentCart.length === 0) {
    alert('⚠️ السلة فارغة');
    return;
  }

  // ✅ إضافة هذا الشرط
  if (getCurrentPaymentMethod() === 'credit') {
    const currentCustomer = getCurrentCustomer();
    if (!currentCustomer) {
      alert('⚠️ لا يمكن تسجيل فاتورة أجل بدون عميل. الرجاء اختيار عميل أولاً.');
      return;
    }
    // منع العميل الافتراضي "عميل صالة"
    if (currentCustomer.name === 'عميل صالة') {
      alert('⚠️ لا يمكن تسجيل فاتورة أجل على "عميل صالة". الرجاء اختيار عميل آخر.');
      return;
    }
  }

    setLoading(true);
    setMessage('');

    try {
      const total = calculateTotal(currentCart, getCurrentDiscount());
      const tax = calculateTax(currentCart);
      const service = calculateService(currentCart);

      const isCredit = getCurrentPaymentMethod() === 'credit';
      const saleStatus = isCredit ? 'partial' : 'paid';
      let paidAmountValue = isCredit ? getCurrentPaidAmount() : total;

      if (applyCustomerCredit && creditToApply > 0 && isCredit) {
        paidAmountValue += creditToApply;
      }

      const saleData: SaleData = {
        customer_id: getCurrentCustomer()?.id,
        customer_name: getCurrentCustomer()?.name,
        customer_phone: getCurrentCustomer()?.phone,
        discount_amount: getCurrentDiscount(),
        payment_method: getCurrentPaymentMethod(),
        payment_status: saleStatus,
        paid_amount: paidAmountValue,
        items: currentCart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price_at_time: item.price,
          cost_at_time: item.cost,
          taxable_at_time: item.apply_tax,
          service_at_time: item.apply_service
        })),
        tax_rate: taxSettings.apply_tax_to_pos ? taxSettings.tax_rate : 0,
        service_rate: taxSettings.apply_service_to_pos ? taxSettings.service_rate : 0,
        total_after_tax: total,
        tax_amount: tax,
        service_amount: service,
        notes: getCurrentCustomer() ? `عميل: ${getCurrentCustomer()?.name}` : 'عميل نقدي',
        apply_customer_credit: applyCustomerCredit,
        credit_to_apply: applyCustomerCredit ? creditToApply : 0
      };

      // الخطوة 1: إنشاء فاتورة مفتوحة
      const openResponse = await api.post(`/api/sales/open`, saleData);
      const saleId = openResponse.data.id;
      const saleNumber = openResponse.data.sale_number;
      const customerCreditUsed = openResponse.data.customer_credit_used || 0;

      // الخطوة 2: إغلاق الفاتورة (لتسجيل القيد المحاسبي)
      await api.post(`/api/sales/${saleId}/close`, {
        payment_method: getCurrentPaymentMethod(),
        paid_amount: paidAmountValue,
        discount_amount: getCurrentDiscount(),
        tax_rate: taxSettings.apply_tax_to_pos ? taxSettings.tax_rate : 0,
        service_rate: taxSettings.apply_service_to_pos ? taxSettings.service_rate : 0,
        notes: getCurrentCustomer() ? `عميل: ${getCurrentCustomer()?.name}` : 'عميل نقدي'
      });

      // طباعة الفاتورة
      const saleDataWithCredit = { ...saleData, sale_number: saleNumber, customer_credit_used: customerCreditUsed };
      printInvoice(saleDataWithCredit, currentCart, calculateSubtotal(currentCart), calculateTax(currentCart), calculateService(currentCart), total);

      // طباعة المطبخ
      if (kitchenSettings.enabled && kitchenSettings.auto_print) {
        printKitchen(currentCart, undefined, 'POS');
        try {
          await api.post('/api/kitchen/orders', {
            sale_number: saleNumber,
            items: currentCart.filter(i => i.is_cafe).map(i => ({
              product_name: i.name_ar,
              quantity: i.quantity,
              notes: i.notes
            }))
          });
        } catch (kitchenError) {
          console.error('❌ Kitchen backend error:', kitchenError);
        }
      }

      resetCurrentCart();
      fetchProducts();

      // تحديث حالة الفواتير المتعددة
      if (activeCart === 'main') {
        if (secondaryCart.length > 0) {
          setHasMultipleInvoices(true);
          setActiveCart('main');
        } else {
          setHasMultipleInvoices(false);
          setActiveCart('main');
        }
      } else {
        if (cart.length > 0) {
          setHasMultipleInvoices(true);
          setActiveCart('main');
        } else {
          setHasMultipleInvoices(false);
          setActiveCart('main');
          localStorage.removeItem(SECONDARY_CART_STORAGE_KEY);
        }
      }

      setMessage('✅ تم إتمام البيع بنجاح');
      setTimeout(() => setMessage(''), 3000);
      maxQuantityCache.current.clear();

    } catch (error: any) {
      console.error('❌ خطأ في إتمام البيع:', error);
      let errorMessage = 'حدث خطأ غير متوقع';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      setMessage(`❌ ${errorMessage}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  }, [
    getCurrentCart, getCurrentDiscount, getCurrentPaymentMethod, getCurrentPaidAmount, getCurrentCustomer,
    calculateTotal, calculateTax, calculateService, calculateSubtotal,
    taxSettings, kitchenSettings, printInvoice, printKitchen,
    resetCurrentCart, fetchProducts, activeCart, cart, secondaryCart, hasMultipleInvoices,
    applyCustomerCredit, creditToApply
  ]);

  const completeSaleWithoutPrint = useCallback(async () => {
  const currentCart = getCurrentCart();
  if (currentCart.length === 0) {
    alert('⚠️ السلة فارغة');
    return;
  }

  // ✅ إضافة هذا الشرط
  if (getCurrentPaymentMethod() === 'credit') {
    const currentCustomer = getCurrentCustomer();
    if (!currentCustomer) {
      alert('⚠️ لا يمكن تسجيل فاتورة أجل بدون عميل. الرجاء اختيار عميل أولاً.');
      return;
    }
    if (currentCustomer.name === 'عميل صالة') {
      alert('⚠️ لا يمكن تسجيل فاتورة أجل على "عميل صالة". الرجاء اختيار عميل آخر.');
      return;
    }
  }

    setLoading(true);
    setMessage('');

    try {
      const total = calculateTotal(currentCart, getCurrentDiscount());
      const tax = calculateTax(currentCart);
      const service = calculateService(currentCart);

      const isCredit = getCurrentPaymentMethod() === 'credit';
      const saleStatus = isCredit ? 'partial' : 'paid';
      let paidAmountValue = isCredit ? getCurrentPaidAmount() : total;

      if (applyCustomerCredit && creditToApply > 0 && isCredit) {
        paidAmountValue += creditToApply;
      }

      const saleData: SaleData = {
        customer_id: getCurrentCustomer()?.id,
        customer_name: getCurrentCustomer()?.name,
        customer_phone: getCurrentCustomer()?.phone,
        discount_amount: getCurrentDiscount(),
        payment_method: getCurrentPaymentMethod(),
        payment_status: saleStatus,
        paid_amount: paidAmountValue,
        items: currentCart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price_at_time: item.price,
          cost_at_time: item.cost,
          taxable_at_time: item.apply_tax,
          service_at_time: item.apply_service
        })),
        tax_rate: taxSettings.apply_tax_to_pos ? taxSettings.tax_rate : 0,
        service_rate: taxSettings.apply_service_to_pos ? taxSettings.service_rate : 0,
        total_after_tax: total,
        tax_amount: tax,
        service_amount: service,
        notes: getCurrentCustomer() ? `عميل: ${getCurrentCustomer()?.name}` : 'عميل نقدي',
        apply_customer_credit: applyCustomerCredit,
        credit_to_apply: applyCustomerCredit ? creditToApply : 0
      };

      // الخطوة 1: إنشاء فاتورة مفتوحة
      const openResponse = await api.post(`/api/sales/open`, saleData);
      const saleId = openResponse.data.id;
      const saleNumber = openResponse.data.sale_number;

      // الخطوة 2: إغلاق الفاتورة (لتسجيل القيد المحاسبي)
      await api.post(`/api/sales/${saleId}/close`, {
        payment_method: getCurrentPaymentMethod(),
        paid_amount: paidAmountValue,
        discount_amount: getCurrentDiscount(),
        tax_rate: taxSettings.apply_tax_to_pos ? taxSettings.tax_rate : 0,
        service_rate: taxSettings.apply_service_to_pos ? taxSettings.service_rate : 0,
        notes: getCurrentCustomer() ? `عميل: ${getCurrentCustomer()?.name}` : 'عميل نقدي'
      });

      // طباعة المطبخ فقط (بدون فاتورة)
      if (kitchenSettings.enabled && kitchenSettings.auto_print) {
        printKitchen(currentCart, undefined, 'POS');
        try {
          await api.post('/api/kitchen/orders', {
            sale_number: saleNumber,
            items: currentCart.filter(i => i.is_cafe).map(i => ({
              product_name: i.name_ar,
              quantity: i.quantity,
              notes: i.notes
            }))
          });
        } catch (kitchenError) {
          console.error('❌ Kitchen backend error:', kitchenError);
        }
      }

      resetCurrentCart();
      fetchProducts();

      // تحديث حالة الفواتير المتعددة
      if (activeCart === 'main') {
        if (secondaryCart.length > 0) {
          setHasMultipleInvoices(true);
          setActiveCart('main');
        } else {
          setHasMultipleInvoices(false);
          setActiveCart('main');
        }
      } else {
        if (cart.length > 0) {
          setHasMultipleInvoices(true);
          setActiveCart('main');
        } else {
          setHasMultipleInvoices(false);
          setActiveCart('main');
          localStorage.removeItem(SECONDARY_CART_STORAGE_KEY);
        }
      }

      setMessage('✅ تم إتمام البيع بنجاح');
      setTimeout(() => setMessage(''), 3000);
      maxQuantityCache.current.clear();

    } catch (error: any) {
      console.error('❌ خطأ في إتمام البيع:', error);
      let errorMessage = 'حدث خطأ غير متوقع';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      setMessage(`❌ ${errorMessage}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  }, [
    getCurrentCart, getCurrentDiscount, getCurrentPaymentMethod, getCurrentPaidAmount, getCurrentCustomer,
    calculateTotal, calculateTax, calculateService,
    taxSettings, kitchenSettings, printKitchen,
    resetCurrentCart, fetchProducts, activeCart, cart, secondaryCart, hasMultipleInvoices,
    applyCustomerCredit, creditToApply
  ]);

  // ========== باقي الكود (التصفية، العرض، المؤثرات) ==========
  const filteredProducts = selectedCategory === 'all'
    ? safeArray<Product>(products)
    : safeFilter<Product>(products, p => p.category_id === Number(selectedCategory));

  const cafeProducts = safeFilter<Product>(filteredProducts, p => p.taxable === true);
  const otherProducts = safeFilter<Product>(filteredProducts, p => p.taxable === false);

  const getProductDisplay = (product: Product) => {
    if (product.product_type === 'recipe') {
      return {
        quantity: product.is_active ? 'متوفر' : 'غير متوفر',
        quantityColor: product.is_active ? '#4CAF50' : '#f44336'
      };
    }
    return {
      quantity: product.stock_quantity,
      quantityColor: product.stock_quantity === 0 ? '#f44336' : '#4CAF50'
    };
  };

  const showLogo = settings.show_logo_in_pos === 'true' && settings.company_logo && settings.company_logo.startsWith('http');

  const currentCartItems = getCurrentCart();
  const currentDiscount = getCurrentDiscount();
  const currentPaymentMethod = getCurrentPaymentMethod();
  const currentPaidAmount = getCurrentPaidAmount();
  const currentCustomer = getCurrentCustomer();

  const productsContainerStyle: React.CSSProperties = {
    height: '300px',
    overflowY: 'auto',
    padding: '10px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #ddd'
  };

  useEffect(() => {
    const savedSidebarState = localStorage.getItem('sidebar_open');
    if (savedSidebarState !== null) {
      setIsSidebarOpen(savedSidebarState === 'true');
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'n' && e.ctrlKey) {
        e.preventDefault();
        openNewInvoice();
        return;
      }
      if (e.key === 'F2') {
        e.preventDefault();
        completeSale();
        return;
      }
      if (e.key === 'F4') {
        e.preventDefault();
        completeSaleWithoutPrint();
        return;
      }
      if (e.key === 'F2' && e.ctrlKey) {
        e.preventDefault();
        setShowSearch(true);
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [completeSale, completeSaleWithoutPrint, openNewInvoice]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowCustomerResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (dataLoadedRef.current) return;
      const token = sessionStorage.getItem('token');
      if (!token) return;
      try {
        console.log('📦 تحميل البيانات...');
        await fetchSettings();
        if (!isMounted) return;
        const taxData = await getTaxSettings();
        if (isMounted) setTaxSettings(taxData);
        if (!isMounted) return;
        await Promise.all([
          fetchProducts(),
          fetchCategories(),
          fetchCustomers()
        ]);
        loadPrinterSettings();
        loadKitchenSettings();
        dataLoadedRef.current = true;
        console.log('✅ تم التحميل');
      } catch (error) {
        console.error('❌ خطأ:', error);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch.length >= 2) {
        searchCustomers();
      } else {
        setCustomerSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  return (
    <div className="App" dir="rtl" style={{ display: 'flex' }}>
      <Sidebar onToggle={(open) => setIsSidebarOpen(open)} />
      <main style={{ 
        flex: 1, 
        padding: '20px', 
        marginRight: isSidebarOpen ? '250px' : '60px',
        transition: 'margin-right 0.3s ease'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          padding: '10px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            {showLogo && (
              <img
                src={settings.company_logo}
                alt="logo"
                style={{ maxHeight: '50px', maxWidth: '150px', objectFit: 'contain' }}
                onError={(e) => {
                  console.error('❌ فشل تحميل اللوجو');
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <h1 style={{ margin: 0, color: '#4CAF50' }}>
              {settings.app_name || 'نظام إدارة المتجر'}
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#2196F3',
              backgroundColor: 'white',
              padding: '8px 15px',
              borderRadius: '5px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              {formatDateTime(currentDateTime)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <DrawerButton />
              <UserInfo />
            </div>
          </div>
        </div>

        {message && (
          <div style={{
            color: message.includes('✅') ? '#155724' : '#721c24',
            padding: '12px 20px',
            backgroundColor: message.includes('✅') ? '#d4edda' : '#f8d7da',
            border: `1px solid ${message.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '5px',
            marginBottom: '20px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            {message}
          </div>
        )}

        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={openNewInvoice}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'background-color 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1976D2'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2196F3'}
            >
              <span>📄</span>
              فاتورة جانبية جديدة
            </button>
            
            {hasMultipleInvoices && (
              <>
                <button
                  onClick={switchToMain}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: activeCart === 'main' ? '#4CAF50' : '#f0f0f0',
                    color: activeCart === 'main' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: activeCart === 'main' ? 'bold' : 'normal',
                    transition: 'all 0.3s'
                  }}
                >
                  الفاتورة الرئيسية ({cart.length})
                </button>
                <button
                  onClick={switchToSecondary}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: activeCart === 'secondary' ? '#FF9800' : '#f0f0f0',
                    color: activeCart === 'secondary' ? 'white' : '#333',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: activeCart === 'secondary' ? 'bold' : 'normal',
                    transition: 'all 0.3s'
                  }}
                >
                  الفاتورة الجانبية ({secondaryCart.length})
                </button>
                <button
                  onClick={closeSecondaryInvoice}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f44336'}
                >
                  <span>✖</span>
                  إغلاق الجانبية
                </button>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1, maxWidth: '400px', position: 'relative' }} ref={searchContainerRef}>
            {currentCustomer ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '5px',
                backgroundColor: '#e8f5e9',
                padding: '5px 10px',
                borderRadius: '20px',
                flex: 1
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                  <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    👤 {currentCustomer.name} - {currentCustomer.phone}
                  </span>
                  <button
                    onClick={clearCustomer}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#f44336',
                      fontSize: '18px',
                      cursor: 'pointer',
                      padding: '0 5px'
                    }}
                  >
                    ×
                  </button>
                </div>
                
                {showCreditAlert && customerCreditBalance > 0 && (
                  <div style={{
                    marginTop: '5px',
                    padding: '6px 10px',
                    backgroundColor: '#fff3e0',
                    borderRadius: '6px',
                    border: '1px solid #FF9800',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%'
                  }}>
                    <span style={{ fontSize: '12px' }}>
                      💰 يوجد رصيد دائن بقيمة <strong>{customerCreditBalance.toFixed(2)} ج.م</strong> من مرتجعات سابقة
                    </span>
                    <button
                      onClick={applyCreditToCurrentSale}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      تطبيق الرصيد
                    </button>
                  </div>
                )}
                
                {applyCustomerCredit && creditToApply > 0 && (
                  <div style={{
                    marginTop: '5px',
                    padding: '6px 10px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px',
                    fontSize: '12px',
                    width: '100%'
                  }}>
                    ✅ تم تطبيق رصيد بقيمة <strong>{creditToApply.toFixed(2)} ج.م</strong> من المرتجعات السابقة
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '5px', flex: 1 }}>
                <input
                  type="text"
                  placeholder="ابحث عن عميل..."
                  value={customerSearch}
                  onChange={handleCustomerSearch}
                  onFocus={() => customerSearch.length >= 2 && setShowCustomerResults(true)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={() => setShowCustomerModal(true)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  ➕ عميل
                </button>
              </div>
            )}

            {showCustomerResults && customerSearchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                left: '0',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto',
                marginTop: '5px'
              }}>
                {customerSearchResults.map(customer => (
                  <div
                    key={customer.id}
                    onClick={() => selectCustomer(customer)}
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div><strong>{customer.name}</strong></div>
                    <div style={{ fontSize: '12px', color: '#666' }}>📞 {customer.phone}</div>
                    {customer.current_balance !== undefined && customer.current_balance > 0 && (
                      <div style={{ fontSize: '11px', color: '#f44336' }}>
                        💰 عليه {customer.current_balance.toFixed(2)} ج.م
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* حقل الباركود */}
        <div style={{
          backgroundColor: '#e8f5e9',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            🔍 إضافة منتج بالباركود:
          </label>
          <input
            type="text"
            placeholder="امسح الباركود أو اكتبه واضغط Enter"
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={handleBarcodeSearch}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #4CAF50',
              borderRadius: '4px',
              fontSize: '16px',
              transition: 'border-color 0.3s'
            }}
            autoFocus
          />
        </div>

        {/* حقل البحث بالاسم العربي */}
        <div style={{ marginBottom: '20px', position: 'relative' }} ref={productSearchRef}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            🔎 بحث بالاسم العربي:
          </label>
          <input
            type="text"
            placeholder="ابحث عن منتج بالاسم العربي..."
            value={productSearchTerm}
            onChange={(e) => {
              setProductSearchTerm(e.target.value);
              setShowProductDropdown(true);
            }}
            onFocus={() => setShowProductDropdown(true)}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #2196F3',
              borderRadius: '4px',
              fontSize: '16px'
            }}
          />
          {showProductDropdown && filteredProductsForSearch.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxHeight: '300px',
              overflowY: 'auto',
              marginTop: '5px'
            }}>
              {filteredProductsForSearch.map(product => {
                const isDisabled = product.product_type === 'recipe' 
                  ? product.is_active === false
                  : product.stock_quantity === 0;
                return (
                  <div
                    key={product.id}
                    onClick={() => {
                      if (!isDisabled) {
                        addToCart(product);
                        setProductSearchTerm('');
                        setShowProductDropdown(false);
                      }
                    }}
                    style={{
                      padding: '10px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                      opacity: isDisabled ? 0.5 : 1,
                      borderBottom: '1px solid #eee',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => !isDisabled && (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => !isDisabled && (e.currentTarget.style.backgroundColor = 'white')}
                  >
                    <div style={{ fontWeight: 'bold' }}>{product.name_ar}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {product.price} ج.م - المخزون: {product.product_type === 'recipe' ? (product.is_active ? 'متوفر' : 'غير متوفر') : product.stock_quantity}
                      {product.barcode && ` - باركود: ${product.barcode}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 'bold' }}>تصفية حسب التصنيف:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              backgroundColor: 'white',
              cursor: 'pointer',
              minWidth: '200px'
            }}
          >
            <option value="all">كل المنتجات</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name_ar}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div style={{ backgroundColor: '#f1f8e9', padding: '15px', borderRadius: '10px' }}>
            <h2 style={{ color: '#4CAF50', marginTop: 0, marginBottom: '15px' }}>☕ الكافيه</h2>
            <div style={productsContainerStyle}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                gap: '10px'
              }}>
                {cafeProducts.map(product => {
                  const display = getProductDisplay(product);
                  const isDisabled = product.product_type === 'recipe' 
                    ? product.is_active === false
                    : product.stock_quantity === 0;
                  return (
                    <button
                      key={product.id}
                      onClick={() => addToCart(product)}
                      disabled={isDisabled}
                      style={{
                        padding: '10px',
                        backgroundColor: isDisabled ? '#ccc' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: isDisabled ? 0.5 : 1,
                        transition: 'transform 0.2s, opacity 0.2s'
                      }}
                      onMouseEnter={(e) => !isDisabled && (e.currentTarget.style.transform = 'translateY(-2px)')}
                      onMouseLeave={(e) => !isDisabled && (e.currentTarget.style.transform = 'translateY(0)')}
                    >
                      <div style={{ fontWeight: 'bold' }}>{product.name_ar}</div>
                      <div style={{ fontSize: '16px' }}>{product.price} ج.م</div>
                      <div style={{ fontSize: '11px', marginTop: '5px', color: display.quantityColor }}>
                        {display.quantity}
                        {product.product_type === 'recipe' && ' (مركب)'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '10px' }}>
            <h2 style={{ color: '#1976D2', marginTop: 0, marginBottom: '15px' }}>📦 أصناف</h2>
            <div style={productsContainerStyle}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                gap: '10px'
              }}>
                {otherProducts.map(product => {
                  const display = getProductDisplay(product);
                  const isDisabled = product.product_type === 'recipe' 
                    ? product.is_active === false
                    : product.stock_quantity === 0;
                  return (
                    <button
                      key={product.id}
                      onClick={() => addToCart(product)}
                      disabled={isDisabled}
                      style={{
                        padding: '10px',
                        backgroundColor: isDisabled ? '#ccc' : '#1976D2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: isDisabled ? 0.5 : 1,
                        transition: 'transform 0.2s, opacity 0.2s'
                      }}
                      onMouseEnter={(e) => !isDisabled && (e.currentTarget.style.transform = 'translateY(-2px)')}
                      onMouseLeave={(e) => !isDisabled && (e.currentTarget.style.transform = 'translateY(0)')}
                    >
                      <div style={{ fontWeight: 'bold' }}>{product.name_ar}</div>
                      <div style={{ fontSize: '16px' }}>{product.price} ج.م</div>
                      <div style={{ fontSize: '11px', marginTop: '5px', color: display.quantityColor }}>
                        {display.quantity}
                        {product.product_type === 'recipe' && ' (مركب)'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          marginTop: '30px',
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0, color: '#333' }}>🛒 سلة المشتريات</h2>
          
          {currentCartItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>🛒</span>
              <p>السلة فارغة</p>
              <p style={{ fontSize: '14px', color: '#999', marginTop: '10px' }}>
                أضف منتجات من القائمة أعلاه
              </p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '12px', textAlign: 'right' }}>المنتج</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>ضريبة</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>خدمة</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>السعر</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>الكمية</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>الإجمالي</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCartItems.map(item => (
                      <tr key={item.product_id} style={{ borderBottom: '1px solid #ddd' }}>
                        <td style={{ padding: '10px', fontWeight: 'bold' }}>{item.name_ar}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={item.apply_tax}
                            onChange={() => toggleItemTaxable(item.product_id)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={item.apply_service}
                            onChange={() => toggleItemService(item.product_id)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{item.price.toFixed(2)} ج.م</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value) || 0)}
                            min="1"
                            max={item.product_type === 'normal' ? item.stock_quantity : undefined}
                            style={{
                              width: '70px',
                              padding: '5px',
                              textAlign: 'center',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                          />
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold', color: '#4CAF50' }}>
                          {(item.price * item.quantity).toFixed(2)} ج.م
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <button
                            onClick={() => removeFromCart(item.product_id)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'background-color 0.3s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d32f2f'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f44336'}
                          >
                            مسح
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>طريقة الدفع:</label>
                  <select
                    value={currentPaymentMethod}
                    onChange={(e) => {
                      const method = e.target.value;
                      setCurrentPaymentMethod(method);
                      if (method !== 'credit') {
                        setCurrentPaidAmount(calculateTotal(currentCartItems, currentDiscount));
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    <option value="cash">💰 كاش</option>
                    <option value="visa">💳 فيزا</option>
                    <option value="wallet">📱 محفظة</option>
                    <option value="credit">📝 أجل</option>
                  </select>
                </div>

                {currentPaymentMethod === 'credit' && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>المبلغ المدفوع (مقدماً):</label>
                    <input
                      type="number"
                      value={currentPaidAmount}
                      onChange={(e) => setCurrentPaidAmount(Number(e.target.value))}
                      min="0"
                      max={calculateTotal(currentCartItems, currentDiscount)}
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <small style={{ color: '#666' }}>
                      المتبقي: {(calculateTotal(currentCartItems, currentDiscount) - currentPaidAmount).toFixed(2)} ج.م
                    </small>
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>قيمة الخصم (ج.م):</label>
                  <input
                    type="number"
                    value={currentDiscount}
                    onChange={(e) => setCurrentDiscount(Number(e.target.value))}
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div style={{ backgroundColor: '#e8f5e9', padding: '15px', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span>الإجمالي قبل الخصم:</span>
                    <span>{calculateSubtotal(currentCartItems).toFixed(2)} ج.م</span>
                  </div>
                  {currentDiscount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', color: '#f44336' }}>
                      <span>قيمة الخصم:</span>
                      <span>- {currentDiscount.toFixed(2)} ج.م</span>
                    </div>
                  )}
                  {applyCustomerCredit && creditToApply > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', color: '#4CAF50' }}>
                      <span>💳 رصيد مستخدم من مرتجعات:</span>
                      <span>- {creditToApply.toFixed(2)} ج.م</span>
                    </div>
                  )}
                  {taxSettings.apply_tax_to_pos && calculateTax(currentCartItems) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', color: '#666' }}>
                      <span>الضريبة ({taxSettings.tax_rate}%):</span>
                      <span>+ {calculateTax(currentCartItems).toFixed(2)} ج.م</span>
                    </div>
                  )}
                  {taxSettings.apply_service_to_pos && calculateService(currentCartItems) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', color: '#666' }}>
                      <span>رسوم الخدمة ({taxSettings.service_rate}%):</span>
                      <span>+ {calculateService(currentCartItems).toFixed(2)} ج.م</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 'bold', borderTop: '2px solid #4CAF50', marginTop: '10px', paddingTop: '10px' }}>
                    <span>الإجمالي النهائي:</span>
                    <span style={{ color: '#4CAF50' }}>{calculateTotal(currentCartItems, currentDiscount).toFixed(2)} ج.م</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={completeSale}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '15px',
                    backgroundColor: loading ? '#ccc' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#45a049')}
                  onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#4CAF50')}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                      <span className="loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '3px' }}></span>
                      جاري إتمام البيع...
                    </span>
                  ) : (
                    <>
                      <span style={{ fontSize: '20px', marginLeft: '5px' }}>🖨️</span>
                      إتمام البيع وطباعة الفاتورة
                      <span style={{ fontSize: '12px', marginRight: '5px', opacity: 0.8 }}>(F2)</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={completeSaleWithoutPrint}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '15px',
                    backgroundColor: loading ? '#ccc' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#1976D2')}
                  onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#2196F3')}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                      <span className="loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '3px' }}></span>
                      جاري إتمام البيع...
                    </span>
                  ) : (
                    <>
                      <span style={{ fontSize: '20px', marginLeft: '5px' }}>💰</span>
                      إتمام البيع بدون طباعة
                      <span style={{ fontSize: '12px', marginRight: '5px', opacity: 0.8 }}>(F4)</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </main>

      {showCustomerModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '350px',
            maxWidth: '90%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, color: '#333' }}>➕ إضافة عميل جديد</h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>الاسم:</label>
              <input
                type="text"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>رقم الهاتف:</label>
              <input
                type="text"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={createCustomer}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4CAF50'}
              >
                إضافة
              </button>
              <button
                onClick={() => setShowCustomerModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f44336'}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {showSearch && (
        <SearchComponent onClose={() => setShowSearch(false)} />
      )}
    </div>
  );
}

export default App;